import { LANDMARK_KINDS } from "../types";
import { angleDifference, distance } from "./geo";
import { featureKey, RoadGraph } from "./graph";
import type { Anchor, Candidate, LocalizationResult, MatcherOptions, Observation, VisualFeature } from "./types";

interface Hypothesis {
    anchorId: string; cost: number; matches: number; skipped: number; rejected: number;
    lastTimestamp: number; staleCount: number;
    pendingDistance: number; distanceVariance: number; distanceKnown: boolean;
    pendingCourse: number; courseVariance: number; courseKnown: boolean;
    evidenceIds: string[]; distinctMatches: number;
}

const OUTLIER_COST = 3.5;

export const emptyResult = (): LocalizationResult => ({
    status: "waiting", candidates: [], candidateCount: 0, processed: 0, unknownWeight: 1,
    limited: false, ignoredDuplicate: false, elapsedMs: 0,
});

function featureCost(observed: Observation["features"][number], expected: VisualFeature): number {
    const confidence = observed.confidence;
    if (observed.kind !== expected.kind) return 3.8 * confidence + 0.8;
    if (observed.kind !== "road_sign") return -Math.log(Math.max(0.1, confidence));
    const [a, b] = [observed.signCode, expected.signCode].map(code => code.trim().replace(/^UA:/i, "").toLowerCase());
    if (a && b && a !== b) return 3.2 * confidence + 0.8;
    const [va, vb] = [observed.signValue, expected.signValue].map(value => value.trim().toLowerCase());
    if (va && vb && va !== vb) return 3.2 * confidence + 0.8;
    return -Math.log(Math.max(0.1, confidence)) + ((!a || !b) ? 0.7 : 0) + ((!!va !== !!vb) ? 0.35 : 0);
}

function observationCost(observation: Observation, anchor: Anchor) {
    const used = new Set<number>();
    let cost = 0, exact = 0;
    // A sorted, one-to-one assignment makes simultaneous object order irrelevant.
    for (const feature of [...observation.features].sort((a, b) => featureKey(a).localeCompare(featureKey(b)))) {
        let best = 3.8 * feature.confidence + 0.8, chosen = -1;
        anchor.features.forEach((expected, index) => {
            const value = featureCost(feature, expected);
            if (!used.has(index) && value < best) { best = value; chosen = index; }
        });
        if (chosen >= 0) used.add(chosen);
        cost += best;
        if (best < 1.5) exact++;
    }
    return { cost, compatible: exact === observation.features.length };
}

function validateObservation(observation: Observation) {
    if (!observation.trackId || observation.trackId.length > 120 || !Number.isFinite(observation.timestampMs) || observation.timestampMs < 0 ||
        !Array.isArray(observation.features) || !observation.features.length || observation.features.length > 8) throw new Error("Некоректне спостереження.");
    for (const feature of observation.features) {
        if (!LANDMARK_KINDS.includes(feature.kind) || typeof feature.signCode !== "string" || typeof feature.signValue !== "string" ||
            !Number.isFinite(feature.confidence) || feature.confidence < 0.05 || feature.confidence > 1) throw new Error("Некоректна ознака спостереження.");
    }
    if (observation.distance && (!Number.isFinite(observation.distance.meters) || observation.distance.meters < 0 ||
        !Number.isFinite(observation.distance.sigmaMeters) || observation.distance.sigmaMeters < 1)) throw new Error("Некоректна оцінка відстані.");
    if (observation.courseChange && (!Number.isFinite(observation.courseChange.degrees) || Math.abs(observation.courseChange.degrees) > 180 ||
        !Number.isFinite(observation.courseChange.sigmaDegrees) || observation.courseChange.sigmaDegrees < 1)) throw new Error("Некоректна оцінка зміни курсу.");
}

/** Bounded Viterbi-style search over oriented landmark states. Weights are
 * relative scores, not calibrated probabilities of being at a GPS position. */
export class LandmarkMatcher {
    private hypotheses: Hypothesis[] = [];
    private seenTracks = new Map<string, number>();
    private processed = 0;
    private firstTimestamp: number | null = null;
    private lastTimestamp = -1;
    private limited = false;
    private result = emptyResult();
    private readonly budget: number;
    private readonly maxSpeed: number;
    private readonly visibility: number;

    constructor(private graph: RoadGraph, private options: MatcherOptions) {
        this.budget = options.maxHypotheses ?? 128;
        this.maxSpeed = options.maxSpeedMps ?? 30;
        this.visibility = options.visibilityRangeMeters ?? 0;
        if (!Number.isFinite(this.visibility) || this.visibility < 0 || this.visibility > 300) throw new Error("Некоректна дальність видимості.");
        if (!Number.isInteger(this.budget) || this.budget < 1 || this.budget > 512 || this.maxSpeed <= 0 || !Number.isFinite(this.maxSpeed)) throw new Error("Некоректні параметри пошуку.");
        if (options.startArea && (options.startArea.center.some(value => !Number.isFinite(value)) || Math.abs(options.startArea.center[0]) > 180 || Math.abs(options.startArea.center[1]) > 85 ||
            !Number.isFinite(options.startArea.radiusMeters) || options.startArea.radiusMeters < 10 || options.startArea.radiusMeters > 20000)) throw new Error("Некоректна стартова область.");
    }

    observe(observation: Observation): LocalizationResult {
        const started = performance.now();
        validateObservation(observation);
        if (observation.timestampMs < this.lastTimestamp) throw new Error("Спостереження мають надходити в хронологічному порядку.");
        this.lastTimestamp = observation.timestampMs;
        const seenAt = this.seenTracks.get(observation.trackId);
        if (seenAt !== undefined && observation.timestampMs - seenAt <= 30000) {
            this.seenTracks.set(observation.trackId, observation.timestampMs);
            return { ...this.result, ignoredDuplicate: true, elapsedMs: performance.now() - started };
        }
        this.seenTracks.set(observation.trackId, observation.timestampMs);
        if (this.seenTracks.size > 256) this.seenTracks.delete(this.seenTracks.keys().next().value!);
        this.firstTimestamp ??= observation.timestampMs;
        this.processed++;
        const costs = new Map(this.graph.anchors.map(anchor => [anchor.id, observationCost(observation, anchor)]));
        const next = new Map<string, Hypothesis>();
        const add = (hypothesis: Hypothesis) => {
            // Coast states keep their own timestamp: merging them with a newly
            // matched state would erase pending motion after a false detection.
            const key = `${hypothesis.anchorId}:${hypothesis.lastTimestamp}`;
            const current = next.get(key);
            if (!current || hypothesis.cost < current.cost) next.set(key, hypothesis);
        };

        for (const hypothesis of this.hypotheses) {
            const source = this.graph.anchorById.get(hypothesis.anchorId)!;
            const pendingDistance = hypothesis.pendingDistance + (observation.distance?.meters ?? 0);
            const variance = hypothesis.distanceVariance + (observation.distance?.sigmaMeters ?? 0) ** 2;
            const distanceKnown = hypothesis.distanceKnown && observation.distance !== null;
            const pendingCourse = hypothesis.pendingCourse + (observation.courseChange?.degrees ?? 0);
            const courseVariance = hypothesis.courseVariance + (observation.courseChange?.sigmaDegrees ?? 0) ** 2;
            const courseKnown = hypothesis.courseKnown && observation.courseChange !== null;
            if (hypothesis.staleCount < 3) add({ ...hypothesis, cost: hypothesis.cost + OUTLIER_COST, rejected: hypothesis.rejected + 1,
                staleCount: hypothesis.staleCount + 1, pendingDistance, distanceVariance: variance, distanceKnown,
                pendingCourse, courseVariance, courseKnown });
            const seconds = (observation.timestampMs - hypothesis.lastTimestamp) / 1000;
            let maxMeters = this.maxSpeed * seconds + 40;
            if (distanceKnown) maxMeters = Math.min(maxMeters, pendingDistance + 5 * Math.sqrt(variance) + 60);
            if (maxMeters > 3000) { this.limited = true; maxMeters = 3000; }
            const tree = this.graph.search(source, maxMeters + this.visibility);
            if (tree.limited) this.limited = true;
            for (const target of this.graph.anchors) {
                const feature = costs.get(target.id)!;
                if (this.visibility && !feature.compatible) continue;
                const routeDistance = (tree.costs.get(target.arcId) ?? Infinity) + target.offset;
                if (!this.visibility && (routeDistance < 1 || routeDistance > maxMeters)) continue;
                const sigma = Math.sqrt(variance + source.accuracyMeters ** 2 + target.accuracyMeters ** 2);
                if (!this.visibility && distanceKnown && Math.abs(routeDistance - pendingDistance) > 5 * sigma + 20) continue;
                const transition = this.visibility ? this.graph.sightingTransition(tree, target, this.visibility, maxMeters) : this.graph.transition(tree, target, maxMeters);
                if (!transition || transition.skipped > 8) continue;
                const newEvidence = target.landmarkIds.some(id => !hypothesis.evidenceIds.includes(id));
                const distanceError = Math.max(0, Math.abs(transition.meters - pendingDistance) - this.visibility);
                const distanceCost = distanceKnown ? 0.5 * (distanceError / Math.max(10, sigma)) ** 2 : 0;
                const courseCost = !this.visibility && courseKnown ? 0.5 * (angleDifference(transition.courseChange, pendingCourse) / Math.max(20, Math.sqrt(courseVariance))) ** 2 : 0;
                const cost = hypothesis.cost + feature.cost + (this.visibility && !newEvidence ? 0.8 : 0) + distanceCost + courseCost + transition.skipped * 0.65;
                add({ anchorId: target.id, cost, matches: hypothesis.matches + (feature.compatible && (!this.visibility || newEvidence) ? 1 : 0),
                    evidenceIds: [...new Set([...hypothesis.evidenceIds, ...target.landmarkIds])].slice(-64),
                    distinctMatches: hypothesis.distinctMatches + (feature.compatible && newEvidence ? 1 : 0),
                    skipped: hypothesis.skipped + transition.skipped, rejected: hypothesis.rejected + (feature.compatible ? 0 : 1),
                    lastTimestamp: observation.timestampMs, staleCount: 0, pendingDistance: 0, distanceVariance: 0, distanceKnown: true,
                    pendingCourse: 0, courseVariance: 0, courseKnown: true });
            }
        }

        // Cold start and recovery evaluate the entire configured dataset. The
        // route generator never supplies a start position to this search.
        for (const anchor of this.graph.anchors) {
            const feature = costs.get(anchor.id)!;
            if (!feature.compatible) continue;
            const area = this.options.startArea;
            const elapsed = (observation.timestampMs - this.firstTimestamp) / 1000;
            if (area && distance(area.center, anchor.coordinate) > area.radiusMeters + this.visibility + elapsed * this.maxSpeed) continue;
            add({ anchorId: anchor.id, cost: (this.processed - 1) * OUTLIER_COST + feature.cost,
                matches: 1, skipped: 0, rejected: this.processed - 1, lastTimestamp: observation.timestampMs, staleCount: 0,
                evidenceIds: [...anchor.landmarkIds], distinctMatches: 1,
                pendingDistance: 0, distanceVariance: 0, distanceKnown: true, pendingCourse: 0, courseVariance: 0, courseKnown: true });
        }

        const ordered = [...next.values()].sort((a, b) => a.cost - b.cost || a.anchorId.localeCompare(b.anchorId));
        // Removing a competitive hypothesis must never manufacture confidence.
        if (ordered.length > this.budget && ordered[this.budget].cost - ordered[0].cost < 8) this.limited = true;
        this.hypotheses = ordered.slice(0, this.budget);
        if (!this.hypotheses.length) {
            this.result = { ...emptyResult(), status: this.limited ? "limited" : "lost", processed: this.processed, limited: this.limited, elapsedMs: performance.now() - started };
            return this.result;
        }

        // Several Viterbi histories ending at the same anchor are not distinct
        // physical positions. Display their best score once.
        const positions = new Map<string, Hypothesis>();
        for (const hypothesis of this.hypotheses) if (!positions.has(hypothesis.anchorId)) positions.set(hypothesis.anchorId, hypothesis);
        const best = this.hypotheses[0];
        const rawUnknown = Math.exp(Math.min(100, best.cost - this.processed * OUTLIER_COST));
        const normalization = [...positions.values()].reduce((sum, hypothesis) => sum + Math.exp(best.cost - hypothesis.cost), rawUnknown);
        const candidates: Candidate[] = [...positions.values()].map(hypothesis => {
            const anchor = this.graph.anchorById.get(hypothesis.anchorId)!;
            const region = this.visibility ? this.graph.visibilityRegion(anchor, this.visibility) : null;
            if (region?.limited) this.limited = true;
            return { anchorId: anchor.id, coordinate: anchor.coordinate, heading: anchor.heading, roadName: anchor.roadName,
                weight: Math.exp(best.cost - hypothesis.cost) / normalization, matches: hypothesis.matches, skipped: hypothesis.skipped, rejected: hypothesis.rejected,
                stale: hypothesis.staleCount > 0,
                uncertaintyMeters: anchor.accuracyMeters + this.visibility + (observation.timestampMs - hypothesis.lastTimestamp) / 1000 * this.maxSpeed,
                ...(region ? { evidenceCount: hypothesis.distinctMatches, positionRegion: region.geometry } : {}) };
        });
        const unknownWeight = rawUnknown / normalization;
        let status: LocalizationResult["status"] = "ambiguous";
        if (unknownWeight > 0.5) status = "lost";
        else if ((this.visibility ? best.distinctMatches : best.matches) < 3) status = "insufficient";
        else if (candidates[0].weight >= 0.9 && !candidates[0].stale && best.matches / (best.matches + best.rejected) >= 0.6) status = "localized";
        if (this.limited) status = "limited";
        this.result = { status, candidates: candidates.filter((candidate, index) => index === 0 || candidate.weight >= 0.005).slice(0, 20), candidateCount: candidates.filter(candidate => candidate.weight >= 0.005).length,
            processed: this.processed, unknownWeight, limited: this.limited, ignoredDuplicate: false, elapsedMs: performance.now() - started };
        return this.result;
    }
}
